<template>
  <div class="sales-wrapper">
    <header class="page-header fade-in">
      <div>
        <h1>Ventes</h1>
        <p>Consultez vos ventes, leur statut et les montants associés.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="refresh" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button class="btn btn-primary" @click="createSale">
          <i class="bi bi-plus-circle"></i>
          <span>Nouvelle vente</span>
        </button>
      </div>
    </header>

    <section class="">
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (référence, client...)"
          />
        </div>
        <div class="stats" v-if="filteredSales.length">
          <span>{{ filteredSales.length }} vente(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des ventes…</p>
      </div>

      <div v-else-if="!filteredSales.length" class="state">
        <i class="bi bi-cash-stack"></i>
        <h3>Aucune vente</h3>
        <p>Ajoutez votre première vente pour commencer le suivi.</p>
        <button class="btn btn-primary" @click="createSale">
          <i class="bi bi-plus-circle"></i>
          <span>Créer une vente</span>
        </button>
      </div>

      <div v-else class="sales-grid">
        <article
          v-for="sale in filteredSales"
          :key="sale.reference || sale.id"
          class="sale-card fade-in"
          @click="viewSale(sale.reference)"
        >
          <div class="card-top">
            <div class="identity">
              <div class="identity-customer">
                <strong class="customer-name">
                  {{
                    sale.customerFullName ||
                    sale.customer_name ||
                    sale.customer_reference ||
                    "Client inconnu"
                  }}
                </strong>
                <span class="customer-total">
                  {{ formatCurrency(sale.totalPriceTTC || sale.total) }}
                </span>
              </div>
              <div class="identity-row">
                <span class="sale-reference">{{ sale.reference }}</span>
                <span class="sale-date">
                  <i class="bi bi-calendar-event"></i>
                  {{ formatDate(sale.date || sale.created_at) }}
                </span>
              </div>
            </div>
          </div>

          <div class="card-details">
            <div class="detail">
              <span class="label"><i class="bi bi-cash-coin"></i>Payé</span>
              <span class="value">{{ formatCurrency(sale.amountPaid) }}</span>
            </div>
            <div class="detail highlight">
              <span class="label"
                ><i class="bi bi-hourglass-split"></i>Restant</span
              >
              <span class="value">{{ formatCurrency(sale.amountUnpaid) }}</span>
            </div>
          </div>

          <div
            v-if="sale.saleItems && sale.saleItems.length > 0"
            class="card-items"
          >
            <div class="items-header">
              <strong>{{ sale.saleItems.length }} article(s)</strong>
            </div>
            <ul class="items-list">
              <li
                v-for="(item, index) in sale.saleItems.slice(0, 2)"
                :key="item.item_reference || index"
                class="item-entry"
              >
                <div class="item-info">
                  <span class="item-name">
                    {{
                      item.itemName ||
                      item.item_name ||
                      item.itemFullName ||
                      item.reference ||
                      "Article"
                    }}
                  </span>
                  <div class="item-details">
                    <span class="item-detail">
                      <i class="bi bi-123"></i>
                      Qty: {{ formatQuantity(item.quantity || item.qty || 0) }}
                    </span>
                    <span class="item-detail">
                      <i class="bi bi-tag"></i>
                      Prix:
                      {{ formatCurrency(item.priceTTC || item.price_ttc || 0) }}
                    </span>
                    <span class="item-detail item-detail--total">
                      <i class="bi bi-cash"></i>
                      Total:
                      {{
                        formatCurrency(
                          item.totalPriceTTC || item.total_price_ttc || 0
                        )
                      }}
                    </span>
                  </div>
                </div>
              </li>
              <li
                v-if="sale.saleItems.length > 2"
                class="item-entry more-items"
              >
                <span>+{{ sale.saleItems.length - 2 }} autre(s)</span>
              </li>
            </ul>
          </div>

          <div class="card-footer">
            <div class="actions">
              <button
                class="btn btn-outline"
                @click.stop="editSale(sale.reference)"
              >
                <i class="bi bi-pencil"></i>
                <span>Modifier</span>
              </button>
              <button
                class="btn btn-danger"
                @click.stop="deleteSale(sale.reference)"
              >
                <i class="bi bi-trash"></i>
                <span>Supprimer</span>
              </button>
              <button
                class="btn btn-print"
                @click.stop="printSale(sale.reference)"
              >
                <i class="bi bi-printer"></i>
                <span>Ticket POS</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <button class="fab" @click="createSale">
      <i class="bi bi-plus-lg"></i>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "SalesList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("sale", {
      sales: "getAll",
    }),
    normalizedSales() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };
      const rawSales = Array.isArray(this.sales?.data)
        ? this.sales.data
        : Array.isArray(this.sales)
        ? this.sales
        : [];

      return rawSales.map((entry) => {
        const reference =
          entry.reference || entry.sale_reference || entry.id || "";
        return {
          id: entry.id || entry.uuid || reference,
          reference,
          customerFullName:
            entry.customerFullName ||
            entry.customer_full_name ||
            entry.customer?.fullName ||
            entry.customer_name ||
            entry.customer_reference ||
            null,
          customer_reference:
            entry.customer_reference || entry.customer_reference || null,
          date: entry.date || entry.sale_date || entry.created_at || null,
          totalPriceTTC: toNumber(
            entry.totalPriceTTC != null
              ? entry.totalPriceTTC
              : entry.total_price_ttc != null
              ? entry.total_price_ttc
              : entry.total_amount != null
              ? entry.total_amount
              : entry.total,
            0
          ),
          totalQuantity: toNumber(
            entry.totalQuantity != null
              ? entry.totalQuantity
              : entry.total_quantity != null
              ? entry.total_quantity
              : entry.items_quantity,
            0
          ),
          amountPaid: toNumber(
            entry.amountPaid != null
              ? entry.amountPaid
              : entry.amount_paid != null
              ? entry.amount_paid
              : entry.paid_amount,
            0
          ),
          amountUnpaid: toNumber(
            entry.amountUnpaid != null
              ? entry.amountUnpaid
              : entry.amount_unpaid != null
              ? entry.amount_unpaid
              : entry.remaining_amount != null
              ? entry.remaining_amount
              : 0,
            0
          ),
          userName: entry.userName || entry.user_name || null,
          remark: entry.remark || null,
          saleItems: entry.saleItems || entry.items || [],
          raw: entry,
        };
      });
    },
    filteredSales() {
      if (!this.searchTerm) {
        return this.normalizedSales;
      }
      const term = this.searchTerm.toLowerCase();
      return this.normalizedSales.filter((sale) => {
        const values = [
          sale.reference,
          sale.customerFullName,
          sale.customer_reference,
          sale.date,
          sale.totalPriceTTC,
          sale.totalQuantity,
        ]
          .filter(
            (value) => value !== null && value !== undefined && value !== ""
          )
          .map((value) => value.toString().toLowerCase());
        return values.some((value) => value.includes(term));
      });
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (error) {
        return value;
      }
    },
    formatQuantity(value) {
      if (!value && value !== 0) return "—";
      const number = Number(value);
      if (Number.isNaN(number)) return value;
      return number % 1 === 0 ? number.toString() : number.toFixed(2);
    },
    async fetchSales() {
      this.loading = true;
      try {
        await this.$store.dispatch("sale/getAll");
      } catch (error) {
        console.error("Error loading sales", error);
        this.$toast.error("Impossible de charger les ventes.");
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchSales();
    },
    createSale() {
      this.$router.push({ name: "sales-create" });
    },
    viewSale(reference) {
      this.$router.push({ name: "sales-show", params: { reference } });
    },
    editSale(reference) {
      this.$router.push({ name: "sales-edit", params: { reference } });
    },
    printSale(reference) {
      if (!reference) return;
      this.$router
        .push({ name: "print", query: { reference } })
        .catch(() => {});
    },
    async deleteSale(reference) {
      if (!reference) return;
      try {
        await this.$store.dispatch("sale/destroy", reference);
        this.$toast.success("Vente supprimée avec succès.");
        this.fetchSales();
      } catch (error) {
        console.error("Error deleting sale", error);
        this.$toast.error("Impossible de supprimer la vente.");
      }
    },
  },
  mounted() {
    if (!this.sales || this.sales.length === 0) {
      this.fetchSales();
    }
  },
};
</script>

<style scoped>
.sales-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
  max-width: 480px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.2rem;
  border-radius: 16px;
  background: #f3f4f8;
  border: 1px solid #e4e7f1;
  min-width: 280px;
  flex: 1;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  width: 100%;
}

.stats {
  color: #6b7280;
  font-weight: 600;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.state i {
  font-size: 3rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.sales-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.sale-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #ffffff 100%);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  min-height: 320px;
}

.sale-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 46px rgba(15, 23, 42, 0.12);
}

.card-top {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.identity-customer {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.customer-name {
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1.2;
}

.customer-total {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2563eb;
}

.identity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sale-reference {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

.sale-date {
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.sale-date i {
  font-size: 0.7rem;
}

.card-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.detail {
  background: #f8faff;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.detail.highlight {
  background: #f0f4ff;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.label i {
  color: #2563eb;
  font-size: 0.8rem;
}

.value {
  color: #0f172a;
  font-weight: 600;
  font-size: 0.85rem;
}

.detail.highlight .value {
  color: #6366f1;
  font-weight: 700;
}

.card-footer {
  margin-top: auto;
}

.card-items {
  padding: 0.6rem;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.15);
}

.items-header {
  margin-bottom: 0.4rem;
  font-size: 0.75rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.items-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 180px;
  overflow-y: auto;
}

.item-entry {
  padding: 0.5rem 0.6rem;
  background: white;
  border-radius: 6px;
  font-size: 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.1);
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.item-name {
  color: #1e293b;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.3;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.item-detail {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 500;
}

.item-detail i {
  color: #2563eb;
  font-size: 0.75rem;
}

.item-detail--total {
  color: #0f172a;
  font-weight: 600;
  margin-top: 0.15rem;
  padding-top: 0.25rem;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
}

.item-detail--total i {
  color: #10b981;
}

.item-entry.more-items {
  justify-content: center;
  color: #64748b;
  font-style: italic;
  background: transparent;
  border: none;
  padding: 0.2rem;
  font-size: 0.7rem;
}

.actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.85rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn i {
  font-size: 1.1rem;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.btn-print {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.btn-print:hover {
  background: rgba(37, 99, 235, 0.2);
}

.fab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #9333ea);
  color: white;
  border: none;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 30px rgba(79, 70, 229, 0.35);
  cursor: pointer;
  transition: transform 0.2s ease;
}

.fab:hover {
  transform: scale(1.1);
}

@media (max-width: 1024px) {
  .sales-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .sales-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .search {
    min-width: unset;
  }
  .sales-grid {
    grid-template-columns: 1fr;
  }
  .card-details {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .page-body {
    padding: 1.25rem;
  }
  .sale-card {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
  .actions {
    flex-direction: column;
  }
}
</style>
